import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { T, getTierFromLumens } from '../lib/constants';
import Av from '../components/Av';
import Bdg from '../components/Bdg';
import Spinner from '../components/Spinner';
import { sanitiseHtml } from '../lib/htmlUtils';
import { useWindowSize } from '../lib/useWindowSize';
import PaperPreview from '../components/PaperPreview';
import FilePreview from '../components/FilePreview';
import MultiImagePreview from '../components/MultiImagePreview';
import LinkPreview, { extractFirstUrl } from '../components/LinkPreview';
import ShareModal from '../components/ShareModal';
import FollowBtn from '../components/FollowBtn';
import { timeAgo } from '../lib/utils';
import MentionAutocomplete from '../components/MentionAutocomplete';
import { detectActiveMention, MentionAndLinkify, notifyMentioned } from '../lib/mentionUtils';

const READING = {
  maxWidth:    680,
  fontSize:    20,
  lineHeight:  1.7,
  paraSpacing: 22,
  titleSize:   42,
  metaSize:    13,
  authorSize:  15,
  sidepadding: 24,
  // Softer reading colour than T.text (#1b1d36 → ~12.6:1 contrast on T.bg
  // is high enough to feel aggressive). #2d2f48 lands at ~9.7:1 — still
  // WCAG AAA but easier on the eye for long-form reading. Used for body
  // copy and headings on the article view; hierarchy is carried by font
  // size + serif, not by colour.
  textColor:   '#2d2f48',
};

function calcReadMins(content) {
  const plain = (content || '').replace(/<[^>]+>/g, '').trim();
  const words = plain ? plain.split(/\s+/).filter(Boolean).length : 0;
  return Math.max(1, Math.round(words / 200));
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
}

function ReadingProgressBar() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el    = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      if (total <= 0) { setProgress(100); return; }
      setProgress(Math.min(100, (el.scrollTop / total) * 100));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div style={{
      position:'fixed', top:0, left:0, right:0,
      height:3, background:T.bdr, zIndex:100,
    }}>
      <div style={{
        height:'100%', width:`${progress}%`,
        background:T.v, transition:'width .1s linear',
      }}/>
    </div>
  );
}

function ArticleHeader({ post, author, readMins, isMobile }) {
  const isDeepDive = post.is_deep_dive === true;
  const plain = (post.content || '').replace(/<[^>]+>/g, '').trim();
  const lines = plain.split('\n').filter(l => l.trim());
  const explicitTitle = (post.deep_dive_title || '').trim();
  const title = isDeepDive
    ? (explicitTitle || (lines[0] && lines[0].length < 120 ? lines[0] : null))
    : null;
  const coverUrl = (isDeepDive && post.deep_dive_cover_url) || '';

  return (
    <header style={{ marginBottom: 32 }}>
      {coverUrl && (
        <img src={coverUrl} alt=""
          style={{
            display:'block', width:'100%', maxWidth:'100%',
            height:'auto', borderRadius:8, marginBottom:28,
          }}/>
      )}
      {title && (
        <h1 style={{
          fontFamily:"'DM Serif Display', serif",
          fontSize: isMobile ? 30 : READING.titleSize,
          color: READING.textColor,
          lineHeight: 1.2,
          margin:'0 0 20px',
          fontWeight: 400,
        }}>
          {title}
        </h1>
      )}
      <div style={{
        display:'flex', alignItems:'center', gap:12,
        paddingBottom: 20, borderBottom:`1px solid ${T.bdr}`,
        marginBottom: 28,
      }}>
        {author?.profile_slug ? (
          <a href={`/p/${author.profile_slug}`} style={{ flexShrink:0, textDecoration:'none' }}>
            <Av size={40} name={author?.name || ''} color={author?.avatar_color || T.v}
              url={author?.avatar_url || ''} tier={author?.tier || null}/>
          </a>
        ) : (
          <Av size={40} name={author?.name || ''} color={author?.avatar_color || T.v}
            url={author?.avatar_url || ''} tier={author?.tier || null}/>
        )}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize: READING.authorSize, fontWeight:700, color:T.text, marginBottom:2 }}>
            {author?.profile_slug ? (
              <a href={`/p/${author.profile_slug}`} style={{ color:T.text, textDecoration:'none' }}>
                {author?.name || 'Unknown'}
              </a>
            ) : (author?.name || 'Unknown')}
          </div>
          <div style={{
            fontSize: READING.metaSize, color: T.mu,
            display:'flex', flexWrap:'wrap', gap:'0 8px',
          }}>
            {author?.title && <span>{author.title}</span>}
            {author?.title && author?.institution && <span>·</span>}
            {author?.institution && <span>{author.institution}</span>}
            {(author?.title || author?.institution) && <span>·</span>}
            <span>{formatDate(post.created_at)}</span>
            {readMins > 0 && <><span>·</span><span>{readMins} min read</span></>}
          </div>
        </div>
      </div>
    </header>
  );
}

function ArticleBody({ post, isDeepDive }) {
  const plain = (post.content || '').replace(/<[^>]+>/g, '').trim();
  const lines = plain.split('\n').filter(l => l.trim());
  const explicitTitle = (post.deep_dive_title || '').trim();
  // Only strip the first content line when the title was implicit.
  // With an explicit deep_dive_title the body content stays untouched.
  const hasExtractedTitle = isDeepDive && !explicitTitle && lines[0] && lines[0].length < 120;

  let bodyHtml = post.content || '';
  if (hasExtractedTitle) {
    bodyHtml = bodyHtml
      .replace(/^<p[^>]*>.*?<\/p>/i, '')
      .replace(/^[^\n<]+(\n|<br\s*\/?>)?/, '')
      .trim();
  }

  return (
    <div
      className="article-body"
      style={{
        fontSize: READING.fontSize,
        lineHeight: READING.lineHeight,
        color: READING.textColor,
        // Source Serif 4 is a true reading serif (regular + bold cuts);
        // DM Serif Display is a display font with only weight 400, so
        // bold can't be visually distinguished from regular.
        fontFamily: isDeepDive
          ? "'Source Serif 4', 'Source Serif Pro', Georgia, serif"
          : "'DM Sans', Arial, sans-serif",
      }}
    >
      {/* Render directly via the sanitiser. Wrapping in <SafeHtml> would
          inject its own outer div with fontSize:13 / lineHeight:1.8 that
          overrides the reading typography defined here. */}
      {bodyHtml && (
        <div dangerouslySetInnerHTML={{ __html: sanitiseHtml(bodyHtml) }}/>
      )}
      <style>{`
        .article-body p { margin: 0 0 ${READING.paraSpacing}px; }
        .article-body h1 {
          font-family: 'DM Serif Display', serif;
          font-size: 32px; color: ${READING.textColor};
          margin: 40px 0 16px; font-weight: 400; line-height: 1.25;
        }
        .article-body h2 {
          font-family: 'DM Serif Display', serif;
          font-size: 26px; color: ${READING.textColor};
          margin: 36px 0 14px; font-weight: 400; line-height: 1.3;
        }
        .article-body h3 {
          font-family: 'DM Sans', sans-serif;
          font-size: 20px; font-weight: 700; color: ${READING.textColor};
          margin: 28px 0 10px;
        }
        .article-body h4 {
          font-family: 'DM Sans', sans-serif;
          font-size: 17px; font-weight: 700; color: ${READING.textColor};
          margin: 22px 0 8px;
        }
        .article-body ul, .article-body ol {
          margin: 0 0 ${READING.paraSpacing}px; padding-left: 26px;
        }
        .article-body li { margin-bottom: 8px; }
        .article-body blockquote {
          border-left: 3px solid ${T.v};
          margin: 24px 0; padding: 4px 0 4px 20px;
          color: ${T.mu}; font-style: italic;
        }
        .article-body a { color: ${T.v}; text-decoration: underline; }
        .article-body img {
          max-width: 100%; height: auto; border-radius: 8px;
          margin: 20px auto; display: block;
        }
        /* img[data-size] resize rules live in PostContentStyles (mounted globally in App.jsx) */
        .article-body iframe {
          width: 100%; max-width: 100%;
          aspect-ratio: 16/9; height: auto;
          border-radius: 8px; margin: 20px 0; display: block; border: 0;
        }
        .article-body strong { font-weight: 700; color: ${READING.textColor}; }
      `}</style>
    </div>
  );
}

function ArticleFooter({ post, author, currentUserId, onShare, onJoinDiscussion }) {
  const isOwn = currentUserId && currentUserId === post.user_id;
  return (
    <footer style={{ marginTop: 48 }}>
      <div style={{ height:1, background:T.bdr, marginBottom:32 }}/>
      <div style={{
        display:'flex', gap:16, alignItems:'flex-start',
        padding:'20px 24px', background:T.w,
        borderRadius:12, border:`1px solid ${T.bdr}`, marginBottom:24,
      }}>
        {author?.profile_slug ? (
          <a href={`/p/${author.profile_slug}`} style={{ flexShrink:0, textDecoration:'none' }}>
            <Av size={52} name={author?.name || ''} color={author?.avatar_color || T.v}
              url={author?.avatar_url || ''} tier={author?.tier || null}/>
          </a>
        ) : (
          <Av size={52} name={author?.name || ''} color={author?.avatar_color || T.v}
            url={author?.avatar_url || ''} tier={author?.tier || null}/>
        )}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:16, fontWeight:700, color:T.text, marginBottom:2 }}>
            {author?.name}
          </div>
          {(author?.title || author?.institution) && (
            <div style={{ fontSize:13, color:T.mu, marginBottom:8 }}>
              {[author?.title, author?.institution].filter(Boolean).join(' · ')}
            </div>
          )}
          {author?.bio && (
            <div style={{
              fontSize:14, color:T.text, lineHeight:1.6, marginBottom:12,
              display:'-webkit-box', WebkitLineClamp:3,
              WebkitBoxOrient:'vertical', overflow:'hidden',
            }}>
              {author.bio}
            </div>
          )}
          {!isOwn && currentUserId && (
            <FollowBtn targetType="user" targetId={post.user_id} currentUserId={currentUserId}/>
          )}
        </div>
      </div>
      <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
        <button onClick={onShare} style={{
          padding:'10px 24px', borderRadius:9,
          border:`1px solid ${T.bdr}`, background:T.w,
          fontSize:14, fontWeight:600, color:T.text,
          cursor:'pointer', fontFamily:'inherit',
          display:'flex', alignItems:'center', gap:6,
        }}>
          ↗ Share
        </button>
        <button onClick={onJoinDiscussion} style={{
          padding:'10px 24px', borderRadius:9, border:'none',
          background:T.v, fontSize:14, fontWeight:600, color:'#fff',
          cursor:'pointer', fontFamily:'inherit',
          display:'flex', alignItems:'center', gap:6,
        }}>
          💬 Join the discussion
        </button>
      </div>
    </footer>
  );
}

function CommentsSection({ post, currentUserId, sectionRef, inputRef: externalInputRef }) {
  const [comments,    setComments]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [text,        setText]        = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [mentionState, setMentionState] = useState(null);
  const internalInputRef = useRef(null);
  const inputRef = externalInputRef || internalInputRef;

  // @-mention plumbing — mirrors PostCard.handleMentionInput.
  const handleMentionInput = (textareaEl, value) => {
    const caret = textareaEl?.selectionStart ?? 0;
    const m = detectActiveMention(value, caret);
    if (!m) { setMentionState(null); return; }
    const r = textareaEl.getBoundingClientRect();
    setMentionState({ query: m.query, start: m.start, caret, top: r.bottom + 4, left: r.left });
  };
  const insertMention = (profile) => {
    if (!mentionState || !profile.profile_slug) return;
    const slug = profile.profile_slug;
    const name = profile.name || slug;
    // Markdown-style marker — render-time shows the full display name,
    // matching the post-side mention rendering.
    const marker = `@[${name}](${slug}) `;
    const next = text.slice(0, mentionState.start) + marker + text.slice(mentionState.caret);
    setText(next);
    const newCaret = mentionState.start + marker.length;
    setTimeout(() => {
      const el = inputRef.current;
      if (el) { el.focus(); el.setSelectionRange(newCaret, newCaret); }
    }, 0);
    setMentionState(null);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('comments')
        .select('*, profiles(name, avatar_color, avatar_url, profile_slug, institution)')
        .eq('post_id', post.id)
        .order('created_at', { ascending: true });
      if (cancelled) return;
      setComments(data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [post.id]);

  const submit = async () => {
    if (!text.trim() || !currentUserId || submitting) return;
    setSubmitting(true);
    const content = text.trim();
    const { data } = await supabase
      .from('comments')
      .insert({ post_id: post.id, user_id: currentUserId, content })
      .select('*, profiles(name, avatar_color, avatar_url, profile_slug, institution)')
      .single();
    if (data) {
      setComments(c => [...c, data]);
      setText('');
      setMentionState(null);
      await notifyMentioned(supabase, content, false, currentUserId, post.id);
    }
    setSubmitting(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    await supabase.from('comments').delete().eq('id', id);
    setComments(c => c.filter(x => x.id !== id));
  };

  return (
    <section ref={sectionRef} style={{ marginTop: 48, scrollMarginTop: 68 }}>
      <div style={{ height:1, background:T.bdr, marginBottom:24 }}/>
      <h2 style={{
        fontFamily:"'DM Serif Display', serif",
        fontSize:22, color:T.text, margin:'0 0 18px',
        fontWeight:400,
      }}>
        Discussion {comments.length > 0 && (
          <span style={{ color:T.mu, fontSize:16, fontFamily:"'DM Sans',sans-serif" }}>
            · {comments.length}
          </span>
        )}
      </h2>

      {loading ? (
        <div style={{ padding:'14px 0', color:T.mu, fontSize:13 }}>Loading comments…</div>
      ) : comments.length === 0 ? (
        <div style={{
          padding:'18px 16px', background:T.w, borderRadius:10,
          border:`1px solid ${T.bdr}`, color:T.mu, fontSize:13.5,
          textAlign:'center',
        }}>
          No comments yet — be the first to reply.
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:14, marginBottom:20 }}>
          {comments.map(c => {
            const removed = !c.profiles;
            return (
            <div key={c.id} style={{
              display:'flex', gap:12, padding:'14px 16px',
              background:T.w, borderRadius:10, border:`1px solid ${T.bdr}`,
              opacity: removed ? 0.85 : 1,
            }}>
              {c.profiles?.profile_slug ? (
                <a href={`/p/${c.profiles.profile_slug}`} style={{ flexShrink:0, textDecoration:'none' }}>
                  <Av size={32} name={c.profiles?.name || ''}
                    color={c.profiles?.avatar_color || T.v}
                    url={c.profiles?.avatar_url || ''}/>
                </a>
              ) : (
                <Av size={32} name={c.profiles?.name || '?'}
                  color={c.profiles?.avatar_color || T.bdr}
                  url={c.profiles?.avatar_url || ''}/>
              )}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'baseline', gap:8, marginBottom:4 }}>
                  <span style={{
                    fontWeight:700, fontSize:13.5,
                    color: removed ? T.mu : T.text,
                    fontStyle: removed ? 'italic' : 'normal',
                  }}>
                    {c.profiles?.profile_slug ? (
                      <a href={`/p/${c.profiles.profile_slug}`} style={{ color:T.text, textDecoration:'none' }}>
                        {c.profiles?.name || 'Unknown'}
                      </a>
                    ) : (c.profiles?.name || (removed ? 'Deleted user' : 'Unknown'))}
                  </span>
                  <span style={{ fontSize:11.5, color:T.mu }}>{timeAgo(c.created_at)}</span>
                  {currentUserId === c.user_id && (
                    <button onClick={() => remove(c.id)} style={{
                      marginLeft:'auto', fontSize:11, color:T.mu,
                      border:'none', background:'transparent',
                      cursor:'pointer', opacity:.6, fontFamily:'inherit',
                    }}>
                      Delete
                    </button>
                  )}
                </div>
                <div style={{
                  fontSize:14, lineHeight:1.6,
                  color: removed ? T.mu : T.text,
                  whiteSpace:'pre-wrap', wordBreak:'break-word',
                }}>
                  <MentionAndLinkify text={c.content}/>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {currentUserId ? (
        <div style={{ display:'flex', gap:10, alignItems:'flex-end' }}>
          <textarea
            ref={inputRef}
            value={text}
            onChange={e => { setText(e.target.value); handleMentionInput(e.target, e.target.value); }}
            onSelect={e => handleMentionInput(e.target, e.target.value)}
            onBlur={() => setTimeout(() => setMentionState(null), 200)}
            onKeyDown={e => {
              // Let the mention dropdown handle Enter when active.
              if (mentionState && e.key === 'Enter') return;
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
            }}
            placeholder="Add to the discussion… type @ to mention someone"
            rows={2}
            style={{
              flex:1, padding:'10px 14px', borderRadius:10,
              border:`1.5px solid ${T.bdr}`, fontSize:14,
              fontFamily:'inherit', outline:'none', resize:'vertical',
              minHeight:44, color:T.text, background:T.w,
            }}
          />
          <button onClick={submit} disabled={submitting || !text.trim()} style={{
            padding:'10px 20px', borderRadius:9, border:'none',
            background: text.trim() && !submitting ? T.v : T.bdr,
            color: text.trim() && !submitting ? '#fff' : T.mu,
            fontSize:14, fontWeight:700, fontFamily:'inherit',
            cursor: text.trim() && !submitting ? 'pointer' : 'default',
          }}>
            {submitting ? '…' : 'Reply'}
          </button>
          {mentionState && (
            <MentionAutocomplete
              query={mentionState.query}
              top={mentionState.top}
              left={mentionState.left}
              onSelect={insertMention}
              onClose={() => setMentionState(null)}
            />
          )}
        </div>
      ) : (
        <div style={{
          padding:'18px 20px', borderRadius:10,
          background:T.v2, border:`1px solid rgba(108,99,255,.2)`,
          textAlign:'center',
        }}>
          <div style={{ fontSize:14, color:T.text, marginBottom:8 }}>
            Sign in to join the discussion.
          </div>
          <a
            href="/"
            onClick={() => sessionStorage.setItem('signup_return_url', window.location.href)}
            style={{
              display:'inline-block', background:T.v, color:'#fff',
              fontWeight:700, fontSize:13, textDecoration:'none',
              borderRadius:22, padding:'8px 22px',
            }}
          >
            Sign in to Luminary →
          </a>
        </div>
      )}
    </section>
  );
}

export default function PublicPostPage({ postId }) {
  const { isMobile } = useWindowSize();
  const [post,          setPost]          = useState(null);
  const [author,        setAuthor]        = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [notFound,      setNotFound]      = useState(false);
  const [accessGated,   setAccessGated]   = useState(false);  // members-only and not authorized to view
  const [sharing,       setSharing]       = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const commentsRef      = useRef(null);
  const commentsInputRef = useRef(null);

  // Auth — public page, but if signed in we enable comment + follow.
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setCurrentUserId(data?.session?.user?.id || null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      setCurrentUserId(s?.user?.id || null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Inject / clean up OG + Twitter meta tags
  useEffect(() => {
    if (!post) return;

    let ogTitle, ogDescription, ogImage;

    if (post.post_type === 'paper' && post.paper_title) {
      ogTitle = post.paper_title;
      const byline   = [post.paper_authors, post.paper_journal, post.paper_year].filter(Boolean).join(' · ');
      const abstract = post.paper_abstract ? post.paper_abstract.slice(0, 250) : '';
      ogDescription  = [byline, abstract].filter(Boolean).join(' — ');
    } else {
      const html  = post.content || '';
      const first = html.split(/<br\s*\/?>|<\/p>|<\/div>/i)[0]
                        .replace(/<[^>]+>/g, '').trim();
      const plain = html.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
      ogTitle = first.slice(0, 100) + (first.length > 100 ? '…' : '');
      ogDescription = plain.slice(0, 280) + (plain.length > 280 ? '…' : '');
    }
    if (post.image_url && post.file_type === 'image') ogImage = post.image_url;
    if (!ogTitle) ogTitle = 'Post on Luminary';
    if (!ogDescription) ogDescription = 'Research networking for scientists and medical affairs professionals.';

    const postUrl = `${window.location.origin}/s/${postId}`;
    const metas = [
      ['property', 'og:type',        'article'],
      ['property', 'og:site_name',   'Luminary'],
      ['property', 'og:title',       ogTitle],
      ['property', 'og:description', ogDescription],
      ['property', 'og:url',         postUrl],
      ...(ogImage ? [['property', 'og:image', ogImage]] : []),
      ['name', 'twitter:card',        ogImage ? 'summary_large_image' : 'summary'],
      ['name', 'twitter:site',        '@LuminaryScience'],
      ['name', 'twitter:title',       ogTitle],
      ['name', 'twitter:description', ogDescription],
      ...(ogImage ? [['name', 'twitter:image', ogImage]] : []),
    ];

    const injected = metas.map(([attr, key, content]) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute('content', content);
      return el;
    });

    return () => { injected.forEach(el => el.remove()); };
  }, [post, postId]);

  useEffect(() => {
    document.title = 'Luminary';
    return () => { document.title = 'Luminary'; };
  }, []);

  // Fetch post + author
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);

      const { data: p } = await supabase
        .from('posts_with_meta').select('*').eq('id', postId).maybeSingle();
      if (cancelled) return;
      if (!p) {
        // RLS denied or genuinely missing. Friendly 404.
        setNotFound(true); setLoading(false); return;
      }

      // Visibility branch (RLS already gated members/private at the row
      // level; this layer enforces the page-render policy):
      //   public  → render for anyone (auth or anon)
      //   members → render only if the caller is signed in (RLS already
      //             confirmed they can read the row); anon visitors get
      //             the friendly access-gated CTA
      //   private → 404 (or render if you're the author, since RLS would
      //             have returned the row)
      const { data: { session } } = await supabase.auth.getSession();
      const callerId = session?.user?.id || null;

      if (p.visibility === 'private' && p.user_id !== callerId) {
        setNotFound(true); setLoading(false); return;
      }
      if (p.visibility === 'members' && !callerId) {
        setAccessGated(true); setPost(p); setLoading(false); return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('id, name, avatar_color, avatar_url, title, institution, bio, profile_slug, lumens_current_period')
        .eq('id', p.user_id).single();

      if (cancelled) return;
      const tier = profile ? getTierFromLumens(profile.lumens_current_period || 0) : null;

      setPost(p);
      setAuthor(profile ? { ...profile, tier } : null);

      const title = p.post_type === 'paper' && p.paper_title
        ? p.paper_title
        : (p.content || '').replace(/<[^>]+>/g, '').trim().slice(0, 80);
      if (title) document.title = `${title} — Luminary`;
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [postId]);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
      height:'100vh', fontFamily:"'DM Sans',sans-serif" }}>
      <Spinner/>
    </div>
  );

  if (notFound) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
      height:'100vh', fontFamily:"'DM Sans',sans-serif", color:T.text, textAlign:'center', padding:'0 24px' }}>
      <div style={{ fontSize:48, marginBottom:16 }}>🔍</div>
      <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:24, marginBottom:8 }}>Post not found</div>
      <div style={{ fontSize:14, color:T.mu, marginBottom:24 }}>This post may have been deleted or is no longer public.</div>
      <a href="/" style={{ color:T.v, fontWeight:600, textDecoration:'none',
        background:T.v2, border:`1px solid rgba(108,99,255,.2)`, borderRadius:8, padding:'8px 18px' }}>
        ← Go to Luminary
      </a>
    </div>
  );

  if (accessGated) {
    const isProject = post?.context_kind === 'project';
    const isGroup   = post?.context_kind === 'group';
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
        height:'100vh', fontFamily:"'DM Sans',sans-serif", color:T.text, textAlign:'center', padding:'0 24px' }}>
        <div style={{ fontSize:48, marginBottom:16 }}>🔒</div>
        <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:24, marginBottom:8 }}>
          {isProject ? 'Project members only' : isGroup ? 'Group members only' : 'Members only'}
        </div>
        <div style={{ fontSize:14, color:T.mu, marginBottom:24, maxWidth: 420 }}>
          This post is in a {isProject ? 'project' : isGroup ? 'group' : 'private space'}.
          Sign in to Luminary to view it{isGroup && post?.group_name ? ` — or request to join ${post.group_name}` : ''}.
        </div>
        <a
          href="/"
          onClick={() => sessionStorage.setItem('signup_return_url', window.location.href)}
          style={{ color:'#fff', background:T.v, fontWeight:700, textDecoration:'none',
            borderRadius:9, padding:'10px 22px', fontSize:14 }}
        >
          Sign in to Luminary →
        </a>
      </div>
    );
  }

  const isDeepDive = post.is_deep_dive === true;
  const readMins   = calcReadMins(post.content);
  const canGoBack  = typeof window !== 'undefined' && window.history.length > 1;

  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'DM Sans',sans-serif", color:T.text }}>
      <ReadingProgressBar/>

      {/* Top bar — kept for unauth visitor wayfinding */}
      <div style={{
        background:T.w, borderBottom:`1px solid ${T.bdr}`,
        padding:'0 24px', display:'flex', alignItems:'center',
        justifyContent:'space-between', height:52,
        position:'sticky', top:0, zIndex:50,
      }}>
        <a href="/" style={{ fontFamily:"'DM Serif Display',serif", fontSize:20, textDecoration:'none', color:T.text }}>
          Lumi<span style={{ color:T.v }}>nary</span>
        </a>
        {!currentUserId && (
          <a
            href="/"
            onClick={() => sessionStorage.setItem('signup_return_url', window.location.href)}
            style={{
              fontSize:12.5, color:T.v, fontWeight:600, textDecoration:'none',
              background:T.v2, border:`1px solid rgba(108,99,255,.2)`,
              borderRadius:8, padding:'7px 16px',
            }}
          >
            Join Luminary →
          </a>
        )}
      </div>

      {canGoBack && (
        <div style={{
          maxWidth: READING.maxWidth + 48, margin:'0 auto',
          padding:'16px 24px 0',
        }}>
          <button onClick={() => window.history.back()} style={{
            background:'none', border:'none', color:T.mu, fontSize:13,
            cursor:'pointer', fontFamily:'inherit', padding:0,
            display:'flex', alignItems:'center', gap:6,
          }}>
            ← Back
          </button>
        </div>
      )}

      <article style={{
        maxWidth: READING.maxWidth, margin:'0 auto',
        padding:`${isMobile ? 20 : 32}px ${isMobile ? 16 : READING.sidepadding}px 0`,
      }}>
        <ArticleHeader post={post} author={author} readMins={readMins} isMobile={isMobile}/>

        <ArticleBody post={post} isDeepDive={isDeepDive}/>

        {/* Link preview */}
        {post.post_type === 'text' && (() => {
          const url = extractFirstUrl(post.content || '');
          return url ? <div style={{ marginTop:20 }}><LinkPreview url={url}/></div> : null;
        })()}

        {/* File attachment */}
        {post.file_deleted_at ? (
          <div style={{
            marginTop:20, padding:'12px 16px', borderRadius:10,
            background:T.s2, border:`1px dashed ${T.bdr}`,
            fontSize:13.5, color:T.mu, fontStyle:'italic',
          }}>
            📎 File removed by author
          </div>
        ) : (post.image_urls?.length > 0) ? (
          <div style={{ marginTop:20 }}>
            <MultiImagePreview urls={post.image_urls}/>
          </div>
        ) : post.image_url ? (
          <div style={{ marginTop:20 }}>
            <FilePreview url={post.image_url} fileType={post.file_type || 'image'} fileName={post.file_name}/>
          </div>
        ) : null}

        {/* Paper card */}
        {post.post_type === 'paper' && post.paper_title && (
          <div style={{ marginTop:28 }}>
            <PaperPreview post={post} currentUserId={currentUserId}/>
          </div>
        )}

        {/* Tags */}
        {(post.tier2?.length > 0 || post.tags?.length > 0) && (
          <div style={{ marginTop:28, paddingTop:18, borderTop:`1px solid ${T.bdr}`,
            display:'flex', gap:6, flexWrap:'wrap' }}>
            {(post.tier2 || []).map(t => (
              <Bdg key={`t2-${t}`} color="v">{t}</Bdg>
            ))}
            {(post.tags || []).map(t => (
              <span key={`tag-${t}`} style={{
                fontSize:11.5, color:T.mu, padding:'2px 9px',
                borderRadius:20, background:T.s2, border:`1px solid ${T.bdr}`,
              }}>
                #{t}
              </span>
            ))}
          </div>
        )}

        <ArticleFooter
          post={post}
          author={author}
          currentUserId={currentUserId}
          onShare={() => setSharing(true)}
          onJoinDiscussion={() => {
            commentsRef.current?.scrollIntoView({ behavior:'smooth', block:'start' });
            setTimeout(() => commentsInputRef.current?.focus(), 400);
          }}
        />

        <CommentsSection post={post} currentUserId={currentUserId} sectionRef={commentsRef} inputRef={commentsInputRef}/>
      </article>

      <div style={{ height: 80 }}/>

      {sharing && <ShareModal post={post} onClose={() => setSharing(false)}/>}
    </div>
  );
}
